import React, { useEffect, useState } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import { ColourGradingDTO, ScoreStatusDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useColourGradingApi } from '../../hooks/useColourGradingApi';

interface ScoreStatusFormProps {
  initialValues: ScoreStatusDTO;
  onSubmit: (data: ScoreStatusDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

const ScoreStatusForm: React.FC<ScoreStatusFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ScoreStatusDTO>(initialValues);

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    colourGradingId: [
      { required: true, message: 'Colour grading is required' },
    ],
    displayPosition: [
      { required: true, message: 'Display Position is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Display Position must be a positive number',
      },
    ],
  };

  const { errors, validate } =
    useFormValidation<ScoreStatusDTO>(validationRules);

  const [colourGradings, setColourGradings] = useState<ColourGradingDTO[]>([]);

  const colourGradingApi = useColourGradingApi();

  const fetchColourGrading = async () => {
    try {
      const response = await colourGradingApi.search({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setColourGradings(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching colour gradings:', error);
    }
  };

  useEffect(() => {
    fetchColourGrading();
  }, []);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
        <FormField
          id="colourGradingId"
          label="Colour Grading"
          type="dropdown"
          value={formData.colourGradingId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select colour grading"
          required={true}
          error={errors.colourGradingId}
          helper="The colour associated with this status."
          options={colourGradings.map((colour) => ({
            value: colour.id,
            label: (
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full inline-block"
                  style={{ backgroundColor: colour.colour }}
                />
                <span>{colour.title}</span>
              </div>
            )
          }))}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          required={true}
          error={errors.displayPosition}
          helper="Lower numbers will be displayed first."
        />
        <FormField
          id="isActive"
          label="Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          required={false}
          error={errors.isActive}
          helper="Check to make record active."
        />
      </FormSection>
    </Form>
  );
};

export default ScoreStatusForm;
